import { useState, useEffect, useCallback, type JSX } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import adminAPI from "./apiClient";
import type { LLMRequest } from "./types";
import { formatDateWithZone } from "../../utils/formatDate";
import { calculateStats, type CalculatedStats } from "./llmRequestFormatting";
import { TabNav, PromptTab, ResponseTab, AnalysisTab, type TabId } from "./LLMRequestPanels";

function BackArrowIcon(): JSX.Element {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function DetailField({ label, value }: { label: string; value: string; }): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-500">{label}</label>
      <p className="mt-1 text-sm text-gray-900">{value}</p>
    </div>
  );
}

function RequestHeader({
  llmRequest,
  stats,
  error,
  onBack,
}: {
  llmRequest: LLMRequest;
  stats: CalculatedStats;
  error: string | null;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700 mr-2"
            title="Back to Action"
          >
            <BackArrowIcon />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">LLM Request Details</h2>
        </div>
        {error && (
          <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
            Using mock data - API not available
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-500">Request ID</label>
          <p className="mt-1 text-sm font-mono text-gray-900">{llmRequest.llm_request_id}</p>
        </div>
        <DetailField label="Model" value={llmRequest.model} />
        <DetailField label="Created At" value={formatDateWithZone(llmRequest.created_at)} />
        <DetailField
          label="Processing Time"
          value={stats.processingTime ? `${stats.processingTime}ms` : "N/A"}
        />
      </div>
    </div>
  );
}

function ActiveTabPanel({
  activeTab,
  llmRequest,
  stats,
}: {
  activeTab: TabId;
  llmRequest: LLMRequest;
  stats: CalculatedStats;
}): JSX.Element {
  if (activeTab === "response") {
    return <ResponseTab llmRequest={llmRequest} stats={stats} />;
  }
  if (activeTab === "analysis") {
    return <AnalysisTab llmRequest={llmRequest} stats={stats} />;
  }
  return <PromptTab llmRequest={llmRequest} stats={stats} />;
}

/** Loads a single LLM request; `error` is surfaced as a banner rather than replacing the view. */
function useLLMRequest(
  conversationId: string | undefined,
  llmRequestId: string | undefined,
): { llmRequest: LLMRequest | null; loading: boolean; error: string | null } {
  const [llmRequest, setLlmRequest] = useState<LLMRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLLMRequestDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLlmRequest(await adminAPI.getLLMRequest(conversationId, llmRequestId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  }, [conversationId, llmRequestId]);

  useEffect(() => {
    void fetchLLMRequestDetails();
  }, [fetchLLMRequestDetails]);

  return { llmRequest, loading, error };
}

export default function LLMRequestDetail(): JSX.Element {
  const { conversationId, turnId, actionId, llmRequestId } = useParams<{
    conversationId: string;
    turnId: string;
    actionId: string;
    llmRequestId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromTab = searchParams.get("fromTab") || "actions";
  const [activeTab, setActiveTab] = useState<TabId>("prompt");
  const { llmRequest, loading, error } = useLLMRequest(conversationId, llmRequestId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!llmRequest) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">LLM request not found.</div>
      </div>
    );
  }

  const stats = calculateStats(llmRequest);
  const handleBackToAction = (): void => {
    void navigate(
      `/admin/conversation/${conversationId}/turn/${turnId}/action/${actionId}?fromTab=${fromTab}`,
    );
  };

  return (
    <div className="space-y-6">
      <RequestHeader
        llmRequest={llmRequest}
        stats={stats}
        error={error}
        onBack={handleBackToAction}
      />

      <div className="bg-white rounded-lg shadow">
        <TabNav activeTab={activeTab} onSelect={setActiveTab} />
        <div className="p-6">
          <ActiveTabPanel activeTab={activeTab} llmRequest={llmRequest} stats={stats} />
        </div>
      </div>
    </div>
  );
}
